import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  User as FirebaseUser,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, googleProvider, db } from '../firebase/config';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<any>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  checkSubscription: () => Promise<boolean>;
  reloadUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => useContext(AuthContext);

// Cache local para dados do usuário
const USER_CACHE_KEY = 'myMoney_userCache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

const getCachedUser = (uid: string): User | null => {
  try {
    const cached = localStorage.getItem(`${USER_CACHE_KEY}_${uid}`);
    if (!cached) return null;
    
    const { data, timestamp } = JSON.parse(cached);
    const now = Date.now();
    
    // Verifica se o cache ainda é válido
    if (now - timestamp < CACHE_DURATION) {
      return data;
    }
    
    // Remove cache expirado
    localStorage.removeItem(`${USER_CACHE_KEY}_${uid}`);
    return null;
  } catch (error) {
    console.error('Erro ao ler cache:', error);
    return null;
  }
};

const setCachedUser = (uid: string, userData: User): void => {
  try {
    const cache = {
      data: userData,
      timestamp: Date.now()
    };
    localStorage.setItem(`${USER_CACHE_KEY}_${uid}`, JSON.stringify(cache));
  } catch (error) {
    console.error('Erro ao salvar cache:', error);
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser: FirebaseUser | null) => {
      try {
        if (firebaseUser) {
          // Tenta usar cache primeiro
          const cachedUser = getCachedUser(firebaseUser.uid);
          
          if (cachedUser) {
            setUser(cachedUser);
            setLoading(false);
            
            // Busca dados atualizados em background
            fetchAndUpdateUserData(firebaseUser, false);
          } else {
            // Sem cache, busca dados normalmente
            await fetchAndUpdateUserData(firebaseUser, true);
          }
        } else {
          setUser(null);
          setLoading(false);
        }
      } catch (error) {
        console.error('Erro no AuthContext:', error);
        setUser(null);
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const fetchAndUpdateUserData = async (firebaseUser: FirebaseUser, updateLoading: boolean) => {
    try {
      let userData = null;
      let userDoc = null;
      
      try {
        userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
        userData = userDoc.data();
      } catch (firestoreError) {
      }
      
      const trialEndDate = userData?.trialEndDate ? 
        (typeof userData.trialEndDate.toDate === 'function' ? userData.trialEndDate.toDate() : new Date(userData.trialEndDate)) :
        new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
      const subscriptionEndDate = userData?.subscriptionEndDate ? 
        (typeof userData.subscriptionEndDate.toDate === 'function' ? userData.subscriptionEndDate.toDate() : new Date(userData.subscriptionEndDate)) :
        null;
      
      const appUser: User = {
        uid: firebaseUser.uid,
      // ...existing code...
      email: firebaseUser.email,
      displayName: firebaseUser.displayName,
      photoURL: firebaseUser.photoURL,
      isPremium: firebaseUser.email === 'jusanmagno@gmail.com' ? true : (userData?.isPremium || false),
      subscriptionEndDate,
      trialEndDate,
      // Dados para IA
      age: userData?.age !== undefined && userData?.age !== null ? Number(userData.age) : undefined,
      occupation: userData?.occupation,
      monthlyIncome: userData?.monthlyIncome !== undefined && userData?.monthlyIncome !== null ? Number(userData.monthlyIncome) : undefined,
      riskProfile: userData?.riskProfile,
      investmentGoals: userData?.investmentGoals,
      // Sistema de indicação
      referralCode: userData?.referralCode,
      referredBy: userData?.referredBy,
      referralCount: userData?.referralCount || 0,
      premiumReferralCount: userData?.premiumReferralCount || 0,
      freeMonthsEarned: userData?.freeMonthsEarned || 0,
      freeMonthsUsed: userData?.freeMonthsUsed || 0,
      // Dados de imposto
      entityType: userData?.entityType,
      profession: userData?.profession
    };

      if (userDoc && !userDoc.exists()) {
        try {
          const newUserCode = firebaseUser.uid.substring(0, 8).toUpperCase();
          await setDoc(doc(db, 'users', firebaseUser.uid), {
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            isPremium: false,
            trialEndDate,
            referralCode: newUserCode,
            referralCount: 0,
            premiumReferralCount: 0,
            freeMonthsEarned: 0,
            freeMonthsUsed: 0,
            createdAt: new Date()
          });
          appUser.referralCode = newUserCode;
        } catch (setDocError) {
          appUser.referralCode = firebaseUser.uid.substring(0, 8).toUpperCase();
        }
      }

      // Salva no cache
      setCachedUser(firebaseUser.uid, appUser);
      setUser(appUser);

      // If notifications already granted, register token automatically
      try {
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          const { requestAndSaveFcmToken } = await import('../firebase/messaging');
          requestAndSaveFcmToken(appUser.uid).catch(e => console.warn('FCM token registration failed', e));
        }
      } catch (e) { console.warn('Erro ao iniciar registro FCM', e); }

    } finally {
      if (updateLoading) {
        setLoading(false);
      }
    }
  };

  const signInWithGoogle = async () => {
    const result = await signInWithPopup(auth, googleProvider);
    return result;
  };

  const signInWithEmail = async (email: string, password: string) => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    // Após login, sincroniza dados do usuário da nuvem
    if (result.user) {
      await reloadUser();
    }
  };

  const signUpWithEmail = async (email: string, password: string) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    return result;
  };

  const signOut = async () => {
    // Remove FCM token from user (if any)
    try {
      if (user && user.uid) {
        const { removeFcmTokenForUser } = await import('../firebase/messaging');
        await removeFcmTokenForUser(user.uid);
      }
    } catch (e) { console.warn('Erro ao remover token FCM no logout', e); }

    // Limpa todos os dados locais relevantes
    Object.keys(localStorage)
      .filter(key =>
        key.startsWith(USER_CACHE_KEY) ||
        key.startsWith('myMoney_servicesCache') ||
        key.startsWith('myMoney_pendingSync') ||
        key.startsWith('myMoney_lastSync')
      )
      .forEach(key => localStorage.removeItem(key));
    await firebaseSignOut(auth);
  };

  const checkSubscription = async (): Promise<boolean> => {
    if (!user) return false;
    
    const now = new Date();
    const trialActive = user.trialEndDate && user.trialEndDate > now;
    const subscriptionActive = user.subscriptionEndDate && user.subscriptionEndDate > now;
    
    return user.isPremium && (!!trialActive || !!subscriptionActive);
  };

  const reloadUser = async () => {
    if (!user) return;
    await fetchAndUpdateUserData({
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL
    } as FirebaseUser, true);
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      signInWithGoogle,
      signInWithEmail,
      signUpWithEmail,
      signOut,
      checkSubscription,
      reloadUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};
